import {
  endOfDay,
  endOfMonth,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Chip } from '@ui/components/Chip'

import { HorizontalScroll } from '@global/HorizontalScroll/HorizontalScroll'

import styles from './RangeOptions.module.scss'

export interface RangeOption {
  label: string
  id: string
  value: () => [Date, Date]
}

interface RangeOptionsProps {
  onRangeSelect: (range: [Date, Date]) => void
  startDate?: Date
  endDate?: Date
  options?: RangeOption[]
}

export const RangeOptions: FC<RangeOptionsProps> = (props) => {
  const { onRangeSelect, startDate, endDate } = props

  const { t } = useTranslation()

  const options: RangeOption[] = useMemo(() => {
    if (props.options) {
      return props.options
    }

    const now = new Date()

    return [
      {
        label: t('Calendar.Today'),
        id: 'today',
        value: () => [startOfDay(now), endOfDay(now)],
      },
      {
        label: t('Calendar.Week'),
        id: 'week',
        value: () => [startOfDay(subDays(now, 7)), endOfDay(now)],
      },
      {
        label: t('Calendar.Last 30 Days'),
        id: 'last-30-days',
        value: () => [startOfDay(subDays(now, 30)), endOfDay(now)],
      },
      {
        label: t('Calendar.This Month'),
        id: 'this-month',
        value: () => [startOfMonth(now), endOfMonth(now)],
      },
      {
        label: t('Calendar.Last Month'),
        id: 'last-month',
        value: () => [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
      },
      {
        label: t('Calendar.Last Year'),
        id: 'last-year',
        value: () => [startOfYear(subYears(now, 1)), endOfYear(subYears(now, 1))],
      },
    ]
  }, [props.options, t])

  const currentTab = useMemo<string | undefined>(() => {
    const selectedOption = options.find((option) => {
      return (
        option.value()[0].getTime() === startDate?.getTime() &&
        option.value()[1].getTime() === endDate?.getTime()
      )
    })
    return selectedOption?.id
  }, [startDate, endDate, options])

  const handleChipClick = (id: string) => {
    const option = options.find((option) => option.id === id)

    if (option) {
      onRangeSelect(option.value())
    }
  }

  return (
    <HorizontalScroll>
      <div className={styles.rangeOptions}>
        {options.map(({ id, label }) => (
          <Chip
            selected={currentTab === id}
            onClick={() => handleChipClick(id)}
            key={id}
            size='sm'
            selectedVariant='neutral'
          >
            {label}
          </Chip>
        ))}
      </div>
    </HorizontalScroll>
  )
}
